import type { TCreateSlice } from "@/store";

import type {
    TMultipropPropertyInfo,
    TNewProperty,
    TNewProject,
} from "@/apis/odin/apiODIN";

import { isNullOrUndefined } from "@/utils/guards";

type TMultipropNewPropertyInfoWithoutDeal = Omit<
    TNewProperty,
    "excelsiorPropertyId" | "projectId" | "databaseId" | "dealName"
>;

type TNewPropertyInfo = TMultipropNewPropertyInfoWithoutDeal;

type TEditingPropertyInfo = Partial<TMultipropNewPropertyInfoWithoutDeal>;

type TStateCreateDeal = {
    dealCreation: {
        // Current step in deal/project creation flow
        currentStep: number;
        // The deal data that is being created
        deal: Omit<TNewProject, "databaseId" | "properties"> | null;
        // Deal properties to be created
        properties: TNewPropertyInfo[];
        // The property being edited in creation flow
        editingProperty: {
            // Index of the property to edit in `properties`, or null if it doesn't exist
            index: number | null;
            // The property that's being edited.
            property: TEditingPropertyInfo;
        } | null;
    };
    multipropCurrentProperty:
        | Partial<TMultipropPropertyInfo>
        | Partial<TNewProperty>; // create or update a property
    multipropEditingProperty: boolean;
    dealCreationDatabaseId: number | null;
    showDatabaseSelectionModal: boolean;
    dealSearchPhrase: string;
    deletePropertyModalOpen: boolean;
    propertyIdForDelete: number | null;
};

type TActionsPublicDeals = {
    dealCreationSetDeal: (
        deal: Exclude<TStateCreateDeal["dealCreation"]["deal"], null>,
    ) => void;
    dealCreationEditNewProperty: () => void;
    dealCreationSaveEditingProperty: () => void;
    dealCreationCancelEditingProperty: () => void;
    dealCreationUpdateEditingProperty: (property: TEditingPropertyInfo) => void;
    dealCreationEditPropertyAtIndex: (propertyIndex: number) => void;
    dealCreationRemovePropertyAtIndex: (propertyIndex: number) => void;

    multipropNewProperty: (params: {
        projectId: number;
        databaseId: number;
    }) => void;
    multipropEditProperty: (property: TMultipropPropertyInfo) => void;
    multipropToggleEditProperty: () => void;
    multipropMutateNewProperty: (data: Partial<TNewProperty>) => void;
    nextStep: () => void;
    previousStep: () => void;
    setDealCreationDatabaseId: (databaseId: number) => void;
    setShowDatabaseSelectionModal: (showModal: boolean) => void;
    setDealSearchPhrase: (phrase: string) => void;
    resetDealCreationState: () => void;
    setDeletePropertyModalStatus: (open: boolean) => void;
    setPropertyIdForDelete: (propertyId: number | null) => void;
};

export type TSliceDeals = TStateCreateDeal & TActionsPublicDeals;

export const initialStateCreateDeal = {
    dealCreation: {
        currentStep: 1,
        deal: null,
        properties: [],
        editingProperty: null,
    },
    multipropCurrentProperty: {},
    multipropEditingProperty: false,
    dealCreationDatabaseId: null,
    showDatabaseSelectionModal: false,
    dealSearchPhrase: "",
    deletePropertyModalOpen: false,
    propertyIdForDelete: null,
} satisfies TStateCreateDeal;

export const createSliceDeals: TCreateSlice<TSliceDeals> = () => set => {
    const actionsPublic: TActionsPublicDeals = {
        dealCreationSetDeal: (
            deal: Exclude<TStateCreateDeal["dealCreation"]["deal"], null>,
        ) => {
            // merges local create/update into one
            set(
                state => ({
                    dealCreation: {
                        ...state.dealCreation,
                        deal: {
                            ...(state.dealCreation.deal ??
                                ({} as Partial<
                                    Exclude<
                                        TStateCreateDeal["dealCreation"]["deal"],
                                        null
                                    >
                                >)),
                            ...deal,
                        },
                    },
                }),
                false,
                "deals/setDealLocal",
            );
        },
        dealCreationEditNewProperty: () => {
            set(
                state => ({
                    dealCreation: {
                        ...state.dealCreation,
                        editingProperty: {
                            index: null,
                            property: {} satisfies TEditingPropertyInfo,
                        },
                    },
                }),
                false,
                "deals/dealCreationEditNewProperty",
            );
        },
        dealCreationSaveEditingProperty: () => {
            set(
                state => {
                    const editingProperty = state.dealCreation.editingProperty;
                    if (editingProperty === null) return state;
                    const index = editingProperty.index;
                    const properties = state.dealCreation.properties;
                    // On save, the property data should be complete.
                    const property =
                        editingProperty.property as TNewPropertyInfo;

                    const newProperties =
                        index === null
                            ? [...properties, property]
                            : properties.toSpliced(index, 1, property);
                    return {
                        dealCreation: {
                            ...state.dealCreation,
                            properties: newProperties,
                            editingProperty: null,
                        },
                    };
                },
                false,
                "deals/dealCreationSaveEditingProperty",
            );
        },
        dealCreationCancelEditingProperty: () => {
            set(
                state => ({
                    dealCreation: {
                        ...state.dealCreation,
                        editingProperty: null,
                    },
                }),
                false,
                "deals/dealCreationCancelEditingProperty",
            );
        },
        dealCreationUpdateEditingProperty: (property: TEditingPropertyInfo) => {
            set(
                state => {
                    const editingProperty = state.dealCreation.editingProperty;
                    if (editingProperty === null) return state;
                    return {
                        dealCreation: {
                            ...state.dealCreation,
                            editingProperty: {
                                ...editingProperty,
                                property: {
                                    ...editingProperty.property,
                                    ...property,
                                },
                            },
                        },
                    };
                },
                false,
                "deals/dealCreationUpdateEditingProperty",
            );
        },
        dealCreationEditPropertyAtIndex: (propertyIndex: number) => {
            set(
                state => {
                    const property =
                        state.dealCreation.properties[propertyIndex];
                    if (property === undefined) return state;
                    return {
                        dealCreation: {
                            ...state.dealCreation,
                            editingProperty: {
                                index: propertyIndex,
                                property: property,
                            },
                        },
                        createNewProperty: true,
                    };
                },
                false,
                "deals/dealCreationEditPropertyAtIndex",
            );
        },
        dealCreationRemovePropertyAtIndex: (propertyIndex: number) => {
            set(
                state => {
                    const properties = [...state.dealCreation.properties];
                    if (
                        propertyIndex < 0 ||
                        propertyIndex >= properties.length
                    ) {
                        return state;
                    }
                    const newProperties = properties.toSpliced(
                        propertyIndex,
                        1,
                    );
                    return {
                        dealCreation: {
                            ...state.dealCreation,
                            properties: newProperties,
                            // Reset this, otherwise it doesn't make sense
                            editingProperty: null,
                        },
                    };
                },
                false,
                "deals/dealCreationRemovePropertyAtIndex",
            );
        },
        multipropMutateNewProperty: (data: Partial<TNewProperty>) => {
            set(
                state => ({
                    multipropCurrentProperty: {
                        ...state.multipropCurrentProperty,
                        ...data,
                    },
                }),
                false,
                "deals/multipropMutateNewProperty",
            );
        },
        multipropNewProperty: ({
            projectId,
            databaseId,
        }: {
            projectId: number;
            databaseId: number;
        }) => {
            if (isNullOrUndefined(projectId)) {
                throw new Error("No project id found");
            }
            set(
                {
                    multipropCurrentProperty: {
                        projectId,
                        databaseId,
                    },
                },
                false,
                "deals/multipropNewProperty",
            );
            set(
                {
                    multipropEditingProperty: true,
                },
                false,
                "deals/multipropNewProperty",
            );
        },
        multipropEditProperty: (property: TMultipropPropertyInfo) => {
            set(
                {
                    multipropCurrentProperty: property,
                },
                false,
                "deals/multipropEditProperty",
            );
            set(
                {
                    multipropEditingProperty: true,
                },
                false,
                "deals/multipropEditProperty",
            );
        },
        multipropToggleEditProperty: () => {
            set(
                state => ({
                    multipropEditingProperty: !state.multipropEditingProperty,
                    createNewProperty: false,
                }),
                false,
                "deals/multipropToggleEditProperty",
            );
        },
        setDealSearchPhrase: phrase => {
            set(
                {
                    dealSearchPhrase: phrase,
                },
                false,
                "deals/setDealSearchPhrase",
            );
        },
        resetDealCreationState: () => {
            set(initialStateCreateDeal, false, "deals/resetDealCreationState");
        },
        setDealCreationDatabaseId: databaseId => {
            set(
                {
                    dealCreationDatabaseId: databaseId,
                },
                false,
                "deals/setDealCreationDatabaseId",
            );
        },

        setShowDatabaseSelectionModal: showModal => {
            set(
                {
                    showDatabaseSelectionModal: showModal,
                },
                false,
                "deals/setShowDatabaseSelectionModal",
            );
        },

        nextStep: () => {
            set(
                state => ({
                    dealCreation: {
                        ...state.dealCreation,
                        currentStep: Math.min(
                            state.dealCreation.currentStep + 1,
                            2,
                        ),
                    },
                }),
                false,
                "deals/nextStep",
            );
        },

        previousStep: () => {
            set(
                state => ({
                    dealCreation: {
                        ...state.dealCreation,
                        currentStep: Math.max(
                            state.dealCreation.currentStep - 1,
                            1,
                        ),
                    },
                }),
                false,
                "deals/previousStep",
            );
        },

        setDeletePropertyModalStatus: open => {
            set(
                {
                    deletePropertyModalOpen: open,
                },
                false,
                "deals/setDeletePropertyModalStatus",
            );
        },
        setPropertyIdForDelete: propertyId => {
            set(
                {
                    propertyIdForDelete: propertyId,
                },
                false,
                "deals/setPropertyIdForDelete",
            );
        },
    };

    return {
        ...initialStateCreateDeal,
        ...actionsPublic,
    };
};
